import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatMoney } from "@/lib/shopify";

export default function ProductCard({ product }) {
  const { addItem, busy } = useCart();
  const [variantId, setVariantId] = useState(
    (product.variants.find((v) => v.availableForSale) || product.variants[0])?.id
  );
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);

  const hasVariants =
    product.variants.length > 1 ||
    (product.variants[0] && product.variants[0].title !== "Default Title");

  const selectedVariant = useMemo(
    () => product.variants.find((v) => v.id === variantId) || product.variants[0],
    [product.variants, variantId]
  );

  const price = selectedVariant?.price || product.minPrice;
  const compareAt = selectedVariant?.compareAtPrice;
  const onSale = compareAt && Number(compareAt.amount) > Number(price.amount);
  const soldOut = !product.availableForSale || !selectedVariant?.availableForSale;

  const image = selectedVariant?.image || product.featuredImage;

  const handleAdd = async () => {
    if (!variantId || soldOut) return;
    setAdding(true);
    try {
      await addItem(variantId, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 1800);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Sorry, we couldn't add that item. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <article
      data-testid={`product-card-${product.handle}`}
      className="group relative flex h-full flex-col bg-charcoal border border-gold/50 overflow-hidden shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)]"
    >
      <span aria-hidden className="pointer-events-none absolute inset-[5px] z-20 border border-gold/25" />
      <span aria-hidden className="pointer-events-none absolute left-1 top-1 z-20 h-3.5 w-3.5 border-l-2 border-t-2 border-gold sm:h-5 sm:w-5" />
      <span aria-hidden className="pointer-events-none absolute right-1 top-1 z-20 h-3.5 w-3.5 border-r-2 border-t-2 border-gold sm:h-5 sm:w-5" />
      <span aria-hidden className="pointer-events-none absolute bottom-1 left-1 z-20 h-3.5 w-3.5 border-b-2 border-l-2 border-gold sm:h-5 sm:w-5" />
      <span aria-hidden className="pointer-events-none absolute bottom-1 right-1 z-20 h-3.5 w-3.5 border-b-2 border-r-2 border-gold sm:h-5 sm:w-5" />

      <Link
        to={`/shop/${product.handle}`}
        aria-label={`View ${product.title}`}
        className="img-zoom relative block aspect-square overflow-hidden bg-onyx"
      >
        {image?.url ? (
          <img
            src={image.url}
            alt={image.altText || product.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain p-3"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gold/40">
            <ShoppingBag size={40} />
          </div>
        )}
        {onSale && !soldOut && (
          <span className="absolute left-3 top-3 z-30 bg-gold px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-onyx">
            Sale
          </span>
        )}
        {soldOut && (
          <span className="absolute inset-0 z-30 flex items-center justify-center bg-onyx/70 text-[0.7rem] uppercase tracking-[0.3em] text-ivory">
            Sold Out
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col px-4 py-5 sm:px-6 sm:py-6">
        <h3 className="font-display text-xl leading-tight text-ivory sm:text-2xl">
          <Link to={`/shop/${product.handle}`} className="transition-colors hover:text-gold">
            {product.title}
          </Link>
        </h3>

        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-gold text-base sm:text-lg" data-testid={`product-price-${product.handle}`}>
            {formatMoney(price.amount, price.currencyCode)}
          </span>
          {onSale && (
            <span className="text-xs text-muted-ivory line-through">
              {formatMoney(compareAt.amount, compareAt.currencyCode)}
            </span>
          )}
        </div>

        {hasVariants && (
          <select
            data-testid={`product-variant-${product.handle}`}
            value={variantId}
            onChange={(e) => setVariantId(e.target.value)}
            className="mt-4 w-full appearance-none border border-gold/40 bg-onyx px-3 py-2.5 text-sm text-ivory outline-none focus:border-gold"
          >
            {product.variants.map((v) => (
              <option key={v.id} value={v.id} disabled={!v.availableForSale}>
                {v.title}
                {!v.availableForSale ? " - Sold out" : ""}
              </option>
            ))}
          </select>
        )}

        <div className="mt-auto w-full pt-5">
          <button
            type="button"
            data-testid={`add-to-cart-${product.handle}`}
            onClick={handleAdd}
            disabled={soldOut || adding || busy}
            className={`inline-flex w-full items-center justify-center gap-2 px-5 py-3 text-[0.7rem] tracking-[0.24em] uppercase transition-all disabled:opacity-50 ${
              added ? "btn-gold" : "btn-gold-outline"
            }`}
          >
            {added ? (
              <>
                <Check size={15} /> Added
              </>
            ) : soldOut ? (
              "Sold Out"
            ) : adding ? (
              "Adding..."
            ) : (
              <>
                <ShoppingBag size={15} /> Add to Cart
              </>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}
